import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  adjustFromTimeZoneSafely,
  adjustToTimeZoneSafely, getRequiredObjectId,
  Pipeline,
  PipelineEntity,
  pipelineSelector,
  useCreatePipelineMutation,
  useGetPipelineQuery,
  useUpdatePipelineMutation
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {notifications} from "@mantine/notifications";
import {PipelineForm, PipelineFormValues} from "../../components/form/PipelineForm/PipelineForm";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: Partial<PipelineFormValues>
}

export const PipelineFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return <PipelineRequestFormPage id={id} copy={copy} formDefaults={formDefaults}/>;
};

export const toFormDefaultsFromPipeline = (pipeline: PipelineEntity | undefined): Partial<PipelineFormValues> => {
  return {
    name: pipeline?.name,
    company: pipeline?.companyId,
    category: pipeline?.category,
    freightTypeCategory: pipeline?.freightTypeCategory,
    allocationType: pipeline?.allocationType,
    inactiveDate: adjustFromTimeZoneSafely(pipeline?.inactiveDttm),
    temporary: pipeline?.temporary,
    doNotUse: pipeline?.doNotUse,
  };
};

export const PipelineRequestFormPage = (props: Props) => {
  const {id, copy = false, formDefaults} = props;

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);

  const selectPipeline = useMemo(() => {
    return pipelineSelector()
  }, [])

  const {currPipeline, isLoading} = useGetPipelineQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currPipeline: selectPipeline(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: Partial<PipelineFormValues> = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromPipeline(currPipeline);
  }, [currPipeline, formDefaults]);

  const isEdit = useMemo(() => {
    return currPipeline && !copy
  }, [currPipeline, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("page.pipeline.form.title.edit", "Edit Pipeline", {ns: "common"});
    } else {
      return t("page.pipeline.form.title.new", "New Pipeline", {ns: "common"});
    }
  }, [isEdit, t]);

  const [createPipeline, {isLoading: isCreating}] = useCreatePipelineMutation();
  const [updatePipeline, {isLoading: isUpdating}] = useUpdatePipelineMutation();

  const onPipelineSubmit = useCallback((formValues: PipelineFormValues) => {
    if (isEdit) {
      const pipeline: Partial<Pipeline> = {
        ...currPipeline.asTypeValue(),
        name: formValues.name,
        companyId: getRequiredObjectId(formValues.company),
        category: formValues.category?.code,
        freightTypeCategory: formValues.freightTypeCategory,
        allocationType: formValues.allocationType?.code,
        inactiveDttm: adjustToTimeZoneSafely(formValues.inactiveDate)?.getTime(),
        temporary: formValues.temporary,
        doNotUse: formValues.doNotUse,
      };
      updatePipeline(pipeline)
        .unwrap()
        .then(() => {
          notifications.show({
            color: "green",
            title: t("common.entity.pipeline", "Pipeline", {ns: "common"}) + " " + t("common.status.updated", "Updated", {ns: "common"}),
            message: ""
          });
          navigate(-1);
        })
        .catch((rejected) =>
          notifications.show({
            color: "red",
            title: t("common.error", "Error", {ns: "common"}),
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
          })
        );
    } else {
      const pipeline: Partial<Pipeline> = {
        name: formValues.name,
        companyId: getRequiredObjectId(formValues.company),
        category: formValues.category?.code,
        freightTypeCategory: formValues.freightTypeCategory,
        allocationType: formValues.allocationType?.code,
        inactiveDttm: adjustToTimeZoneSafely(formValues.inactiveDate)?.getTime(),
        temporary: formValues.temporary,
        doNotUse: formValues.doNotUse,
      };
      createPipeline(pipeline)
        .unwrap()
        .then((payload) => {
          notifications.show({
            color: "green",
            title: t("common.entity.pipeline", "Pipeline", {ns: "common"}) + " " + t("common.status.created", "Created", {ns: "common"}),
            message: ""
          });
          navigate(`/pipelines/profile?id=${payload.id}`, {replace: true});
        })
        .catch((rejected) =>
          notifications.show({
            color: "red",
            title: t("common.error", "Error", {ns: "common"}),
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
          })
        );
    }
  }, [currPipeline, isEdit, updatePipeline, t, navigate, createPipeline]);

  return (
    <ProtectedPage
      permissions={[isEdit ? "pipeline_edit" : "pipeline_create"]}
      isLoading={isLoading}
      onAccess={setAccess}>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <PipelineForm
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onPipelineSubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ProtectedPage>
  );
};

